import React from "react";
import { Stage, Layer, Rect, Group, Text, Tag, Label } from "react-konva";
import Konva from "konva";
import { useEffect } from "react";
import {
  ComponentGraphicData,
  ComponentInsightsOUT,
  GraphicOrientation,
} from "api-client";
import ArrowLeft from "assets/images/icons/ArrowLeftLong.svg";
import ArrowTop from "assets/images/icons/ArrowTopLong.svg";
import ArrowBottom from "assets/images/icons/ArrowBottomLong.svg";
import ArrowRight from "assets/images/icons/ArrowRightLong.svg";
import clsx from "clsx";
import { COEStore } from "../ComponentEditorStore";
import { observer } from "mobx-react-lite";
import { IconCube } from "@tabler/icons-react";

export interface PatternDictionary {
  [id: string]: HTMLImageElement;
}

const CONTAINER_SIZE = 200;
const LABEL_FONT_SIZE = 16;
const LABEL_PADDING = 5;
const LABEL_SIZE = LABEL_FONT_SIZE + LABEL_PADDING * 2;

const getGroupX = (index: number, layers: ComponentGraphicData[]) => {
  let accumulatedX = 0;
  for (let i = 0; i < index; i++) {
    accumulatedX += Number(layers?.[i]?.percentage) * CONTAINER_SIZE;
  }

  return accumulatedX;
};

const getGroupSize = (percentage: number) => {
  return Number(percentage) * CONTAINER_SIZE;
};

interface LayerCoordinates {
  x: number;
  y: number;
  width: number;
  height: number;
}

function layerCoordinates(
  orientation: GraphicOrientation,
  index: number,
  percentage: number,
  container_size: number,
  layers: ComponentGraphicData[]
): LayerCoordinates {
  const position = getGroupX(index, layers);
  const size = Number(percentage) * container_size;

  if (orientation === GraphicOrientation.Horizontal) {
    return {
      x: position,
      y: 0,
      width: size,
      height: container_size,
    };
  } else {
    return {
      x: 0,
      y: position,
      width: container_size,
      height: size,
    };
  }
}

function renderComponentPart(
  part: ComponentGraphicData,
  index: number,
  orientation: GraphicOrientation,
  patternImages: PatternDictionary,
  containerCoordinates: LayerCoordinates
) {
  /**
   * This function will render a building product. Inside there could be many layers.
   */

  const isHorizontal = orientation === GraphicOrientation.Horizontal;
  const epdOrientation = isHorizontal
    ? GraphicOrientation.Vertical
    : GraphicOrientation.Horizontal;

  const container_size = isHorizontal
    ? containerCoordinates.height
    : containerCoordinates.width;

  return (
    <Group key={index} rotation={0} x={0} y={0}>
      {part.graphic_parts?.map((layer, idx) => {
        const epdCoordinates = layerCoordinates(
          epdOrientation,
          idx,
          Number(layer.percentage),
          container_size,
          part.graphic_parts || []
        );
        return (
          <>
            {layer.fill_color && (
              <Rect
                key={`fill_${layer.epd_id}`}
                width={epdCoordinates.width}
                height={epdCoordinates.height}
                x={epdCoordinates.x}
                y={epdCoordinates.y}
                fill={layer.fill_color}
                strokeWidth={0.3}
                stroke="darkgray"
              />
            )}
            {layer.fill_pattern && (
              <Rect
                key={`pattern_${layer.epd_id}`}
                width={epdCoordinates.width}
                height={epdCoordinates.height}
                x={epdCoordinates.x}
                y={epdCoordinates.y}
                fillPatternImage={patternImages[layer.fill_pattern]}
                fillPatternRotation={isHorizontal ? 90 : 0}
                strokeWidth={0.3}
                stroke="darkgray"
                fillPatternScaleX={0.5}
                fillPatternScaleY={0.5}
              />
            )}
          </>
        );
      })}
    </Group>
  );
}

const GraphicObject = ({
  info,
  patternImages,
  orientation,
  countOfProducts,
}: {
  info?: ComponentInsightsOUT | null;
  patternImages: PatternDictionary;
  orientation: GraphicOrientation;
  countOfProducts: number;
}) => {
  const stageRef = React.useRef<Konva.Stage>(null);

  function dataURLtoFile(dataURL: string, filename: string): File {
    const arr = dataURL.split(",");
    const mime = arr[0].match(/:(.*?);/)![1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], filename, { type: mime });
  }

  function generateImageFromGraphic() {
    if (!info?.component_graphic?.length) return;

    const uri = stageRef.current?.toDataURL({ pixelRatio: 2 });
    if (uri) {
      const timestamp = new Date().toLocaleDateString("de-DE");
      const file = dataURLtoFile(uri, `Produktgrafik_${timestamp}.png`);
      COEStore.setDiagramsToUpload(file);
    }
  }

  function visibleLabels(show: boolean) {
    stageRef.current?.find("Label").forEach((label) => {
      if (show) {
        label.show();
      } else {
        label.hide();
      }
    });
  }

  useEffect(() => {
    if (!stageRef.current) return;
    // clear any previous diagram
    COEStore.setDiagramsToUpload(undefined);

    const timeout = setTimeout(() => {
      visibleLabels(false);
      generateImageFromGraphic();
      visibleLabels(true);
    }, 100);

    return () => clearTimeout(timeout);
  }, [patternImages, info?.component_graphic, stageRef?.current]);

  function productsCountChip() {
    return (
      <div
        className={clsx(
          "bg-teal-300 border border-gray-300 text-teal-900 rounded-full flex items-center justify-center min-w-[39px] min-h-[23px]",
          "absolute right-[10px] bottom-[10px] px-2 py-0.5"
        )}
      >
        <span className="text-base font-semibold mr-1">{countOfProducts}</span>
        <IconCube size={18} stroke={3} />
      </div>
    );
  }

  function footer() {
    return (
      <div
        className={clsx("flex items-center", {
          "flex-col": orientation == GraphicOrientation.Vertical,
        })}
      >
        <img
          src={
            orientation == GraphicOrientation.Vertical ? ArrowTop : ArrowLeft
          }
          alt="arrow left"
          className={
            orientation == GraphicOrientation.Vertical ? "h-full" : "w-full"
          }
        />
        <span className="text-xs font-medium text-gray-500 p-2 whitespace-nowrap">
          {info?.total_thickness} mm
        </span>
        <img
          src={
            orientation == GraphicOrientation.Vertical
              ? ArrowBottom
              : ArrowRight
          }
          alt="arrow right"
          className={
            orientation == GraphicOrientation.Vertical ? "h-full" : "w-full"
          }
        />
      </div>
    );
  }

  if (!patternImages || !info?.component_graphic?.length) return null;
  return (
    <div
      className={clsx("rounded-md overflow-hidden", {
        "flex-col": orientation != GraphicOrientation.Vertical,
        flex: orientation == GraphicOrientation.Vertical,
      })}
    >
      <div className="border border-gray-300 rounded-md overflow-hidden relative">
        <Stage
          width={CONTAINER_SIZE}
          height={CONTAINER_SIZE}
          ref={stageRef}
          rotation={0}
          x={0}
          y={0}
        >
          {info.component_graphic.map((part, index) => {
            const coordinates = layerCoordinates(
              orientation,
              index,
              Number(part.percentage),
              CONTAINER_SIZE,
              info.component_graphic
            );
            return (
              <Layer key={index}>
                <Group
                  x={coordinates.x}
                  y={coordinates.y}
                  width={coordinates.width}
                  height={coordinates.height}
                >
                  {renderComponentPart(
                    part,
                    index,
                    orientation,
                    patternImages,
                    coordinates
                  )}
                  {getGroupSize(Number(part.percentage)) > 30 && (
                    <Label
                      x={coordinates.width / 2}
                      y={coordinates.height / 2}
                      offsetX={LABEL_SIZE / 2}
                      offsetY={LABEL_SIZE / 2}
                      rotation={0}
                    >
                      <Tag
                        fill="#5EEAD4"
                        cornerRadius={100}
                        strokeWidth={1}
                        width={LABEL_SIZE}
                        height={LABEL_SIZE}
                      />
                      <Text
                        text={`${Number(index) + 1}`}
                        fontSize={LABEL_FONT_SIZE}
                        fontStyle="bold"
                        fill="#134e4a"
                        padding={LABEL_PADDING}
                        width={LABEL_SIZE}
                        height={LABEL_SIZE}
                        verticalAlign="middle"
                        align="center"
                      />
                    </Label>
                  )}
                </Group>
              </Layer>
            );
          })}
        </Stage>
        {productsCountChip()}
      </div>
      {footer()}
    </div>
  );
};

export default observer(GraphicObject);
